import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './SimpleEditor.css';

const BasicEditor = forwardRef(({ value, onChange, placeholder, rows = 3, showToolbar = true, ...props }, ref) => {
  const { t } = useTranslation();
  const editorRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getContent: () => editorRef.current?.innerHTML || '',
    setContent: (html) => {
      if (editorRef.current) {
        editorRef.current.innerHTML = html;
      }
    }
  }), []);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (onChange && editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (onChange && editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;');
      emitChange();
    }
  }, [emitChange]);

  const exec = useCallback((cmd, arg = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(cmd, false, arg);
    emitChange();
  }, [emitChange]);

  const onInsertLink = useCallback(() => {
    const url = window.prompt(t('Inserisci URL'), 'https://');
    if (url) exec('createLink', url);
  }, [t, exec]);

  const onInsertImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (!file) return;
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(t('File size should not exceed 5MB'));
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        if (editorRef.current) {
          editorRef.current.focus();
          
          const img = document.createElement('img');
          img.src = reader.result;
          img.style.cssText = 'max-width: 100%; height: auto; display: block; margin: 10px auto;';
          img.alt = 'image';
          
          const range = window.getSelection().getRangeAt(0);
          range.deleteContents();
          range.insertNode(img);
          
          range.setStartAfter(img);
          range.collapse(true);
          window.getSelection().removeAllRanges();
          window.getSelection().addRange(range);
          
          emitChange();
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [t, emitChange]);

  return (
    <div className='simple-editor'>
      {showToolbar && (
        <div className='simple-editor-toolbar'>
          <button type='button' className='toolbar-button' onClick={() => exec('bold')}><b>B</b></button>
          <button type='button' className='toolbar-button' onClick={() => exec('italic')}><i>I</i></button>
          <button type='button' className='toolbar-button' onClick={() => exec('underline')}><u>U</u></button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={() => exec('formatBlock', 'H1')}>H1</button>
          <button type='button' className='toolbar-button' onClick={() => exec('formatBlock', 'H2')}>H2</button>
          <button type='button' className='toolbar-button' onClick={() => exec('formatBlock', 'P')}>P</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={() => exec('insertUnorderedList')}>• List</button>
          <button type='button' className='toolbar-button' onClick={() => exec('insertOrderedList')}>1. List</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={() => exec('justifyLeft')}>⟸</button>
          <button type='button' className='toolbar-button' onClick={() => exec('justifyCenter')}>⇔</button>
          <button type='button' className='toolbar-button' onClick={() => exec('justifyRight')}>⟹</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={onInsertLink}>Link</button>
          <button type='button' className='toolbar-button' onClick={onInsertImage} title={t('Carica immagine dal computer (max 5MB)')}>{t('Immagine')}</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={() => exec('removeFormat')}>{t('Rimuovi')}</button>
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className='simple-editor-content'
        style={{
          minHeight: `${rows * 24}px`,
        }}
        {...props}
      />
    </div>
  );
});

export default BasicEditor;

